import { create } from 'zustand';
import { devtools } from 'zustand/middleware';


export interface LoadingState {
  isLoading: boolean;
  operation: string | null;
  progress?: number;
  error?: string | null;
  success?: boolean;
}

export interface LoadingStore {
  // State
  loadingStates: Record<string, LoadingState>;
  globalLoading: boolean;
  
  // Actions
  setLoading: (key: string, loading: boolean, operation?: string) => void;
  setProgress: (key: string, progress: number) => void;
  setError: (key: string, error: string | null) => void;
  setSuccess: (key: string, success: boolean) => void;
  getLoading: (key: string) => LoadingState;
  isAnyLoading: () => boolean;
  clearLoading: (key: string) => void;
  clearAll: () => void;
  
  // Global loading
  setGlobalLoading: (loading: boolean) => void;
  
  // Batch operations
  setMultipleLoading: (keys: string[], loading: boolean, operation?: string) => void;
}

export const useLoadingStore = create<LoadingStore>()(
  devtools(
    (set, get) => ({
      loadingStates: {},
      globalLoading: false,
      
      setLoading: (key: string, loading: boolean, operation?: string) => {
        set((state) => ({
          loadingStates: {
            ...state.loadingStates,
            [key]: {
              isLoading: loading,
              operation: loading ? operation || key : null,
              progress: loading ? 0 : undefined,
              error: loading ? null : state.loadingStates[key]?.error,
              success: loading ? false : state.loadingStates[key]?.success,
            },
          },
          globalLoading: loading || Object.values({ ...state.loadingStates, [key]: { isLoading: loading } }).some(s => s.isLoading),
        }));
      },
      
      setProgress: (key: string, progress: number) => {
        set((state) => ({
          loadingStates: {
            ...state.loadingStates,
            [key]: {
              ...state.loadingStates[key],
              progress,
            },
          },
        }));
      },
      
      setError: (key: string, error: string | null) => {
        set((state) => ({
          loadingStates: {
            ...state.loadingStates,
            [key]: {
              ...state.loadingStates[key],
              error,
              isLoading: false,
            },
          },
        }));
      },
      
      setSuccess: (key: string, success: boolean) => {
        set((state) => ({
          loadingStates: {
            ...state.loadingStates,
            [key]: {
              ...state.loadingStates[key],
              success,
              isLoading: false,
            },
          },
        }));
      },
      
      getLoading: (key: string) => {
        return get().loadingStates[key] || { isLoading: false, operation: null };
      },
      
      isAnyLoading: () => {
        const states = get().loadingStates;
        return Object.values(states).some(state => state.isLoading);
      },
      
      clearLoading: (key: string) => {
        set((state) => {
          const newStates = { ...state.loadingStates };
          delete newStates[key];
          return {
            loadingStates: newStates,
            globalLoading: Object.values(newStates).some(s => s.isLoading),
          };
        });
      },
      
      clearAll: () => {
        set({ loadingStates: {}, globalLoading: false });
      },
      
      setGlobalLoading: (loading: boolean) => {
        set({ globalLoading: loading });
      },
      
      setMultipleLoading: (keys: string[], loading: boolean, operation?: string) => {
        set((state) => {
          const newStates = { ...state.loadingStates };
          keys.forEach(key => {
            newStates[key] = {
              isLoading: loading,
              operation: loading ? operation || key : null,
              progress: loading ? 0 : undefined,
              error: loading ? null : newStates[key]?.error,
              success: loading ? false : newStates[key]?.success,
            };
          });
          return {
            loadingStates: newStates,
            globalLoading: loading || Object.values(newStates).some(s => s.isLoading),
          };
        });
      },
    }),
    {
      name: 'loading-store',
    }
  )
);

// Predefined loading keys for different operations
export const LOADING_KEYS = {
  // Auth operations
  AUTH_LOGIN: 'auth_login',
  AUTH_LOGOUT: 'auth_logout',
  AUTH_REFRESH: 'auth_refresh',
  
  // Order operations
  ORDER_CREATE: 'order_create',
  ORDER_UPDATE: 'order_update',
  ORDER_DELETE: 'order_delete',
  ORDER_FETCH: 'order_fetch',
  ORDER_FETCH_ALL: 'order_fetch_all',
  ORDER_FETCH_BY_ID: 'order_fetch_by_id',
  
  // Customer operations
  CUSTOMER_CREATE: 'customer_create',
  CUSTOMER_UPDATE: 'customer_update',
  CUSTOMER_DELETE: 'customer_delete',
  CUSTOMER_FETCH: 'customer_fetch',
  CUSTOMER_FETCH_ALL: 'customer_fetch_all',
  CUSTOMER_FETCH_BY_ID: 'customer_fetch_by_id',
  
  // Site operations
  SITE_CREATE: 'site_create',
  SITE_UPDATE: 'site_update',
  SITE_DELETE: 'site_delete',
  SITE_FETCH: 'site_fetch',
  SITE_FETCH_ALL: 'site_fetch_all',
  SITE_FETCH_BY_ID: 'site_fetch_by_id',
  
  // User operations
  USER_CREATE: 'user_create',
  USER_UPDATE: 'user_update',
  USER_DELETE: 'user_delete',
  USER_FETCH: 'user_fetch',
  USER_FETCH_ALL: 'user_fetch_all',
  USER_FETCH_BY_ID: 'user_fetch_by_id',
  
  // Product operations
  PRODUCT_CREATE: 'product_create',
  PRODUCT_UPDATE: 'product_update',
  PRODUCT_DELETE: 'product_delete',
  PRODUCT_FETCH: 'product_fetch',
  PRODUCT_FETCH_ALL: 'product_fetch_all',
  PRODUCT_FETCH_BY_ID: 'product_fetch_by_id',
  
  // Branch operations
  BRANCH_CREATE: 'branch_create',
  BRANCH_UPDATE: 'branch_update',
  BRANCH_DELETE: 'branch_delete',
  BRANCH_FETCH: 'branch_fetch',
  BRANCH_FETCH_ALL: 'branch_fetch_all',
  BRANCH_FETCH_BY_ID: 'branch_fetch_by_id',
  
  // Workflow operations
  WORKFLOW_CREATE: 'workflow_create',
  WORKFLOW_UPDATE: 'workflow_update',
  WORKFLOW_DELETE: 'workflow_delete',
  WORKFLOW_FETCH: 'workflow_fetch',
  WORKFLOW_FETCH_ALL: 'workflow_fetch_all',
  
  // Form operations
  FORM_SUBMIT: 'form_submit',
  FORM_SAVE: 'form_save',
  FORM_VALIDATE: 'form_validate',
  
  // File operations
  FILE_UPLOAD: 'file_upload',
  FILE_DOWNLOAD: 'file_download',
  FILE_DELETE: 'file_delete',
  
  // Search operations
  SEARCH_GLOBAL: 'search_global',
  SEARCH_LOCAL: 'search_local',
  
  // Export operations
  EXPORT_DATA: 'export_data',
  EXPORT_PDF: 'export_pdf',
  EXPORT_EXCEL: 'export_excel',
  
  // Import operations
  IMPORT_DATA: 'import_data',
  IMPORT_CSV: 'import_csv',
  
  // Sync operations
  SYNC_DATA: 'sync_data',
  SYNC_OFFLINE: 'sync_offline',
  
  // Batch operations
  BATCH_CREATE: 'batch_create',
  BATCH_UPDATE: 'batch_update',
  BATCH_DELETE: 'batch_delete',
  
  // Analytics operations
  ANALYTICS_FETCH: 'analytics_fetch',
  ANALYTICS_GENERATE: 'analytics_generate',
  ANALYTICS_EXPORT: 'analytics_export',
} as const;

// Hook for easy loading state management
export const useLoading = (key: string) => {
  const { getLoading, setLoading, setError, setSuccess, clearLoading } = useLoadingStore();
  
  const loadingState = getLoading(key);
  
  const startLoading = (operation?: string) => setLoading(key, true, operation);
  const stopLoading = () => setLoading(key, false);
  const setLoadingError = (error: string) => setError(key, error);
  const setLoadingSuccess = (success: boolean) => setSuccess(key, success);
  const clearLoadingState = () => clearLoading(key);
  
  return {
    ...loadingState,
    startLoading,
    stopLoading,
    setLoadingError,
    setLoadingSuccess,
    clearLoadingState,
  };
};

// Hook for multiple loading states
export const useMultipleLoading = (keys: string[]) => {
  const { setMultipleLoading, isAnyLoading } = useLoadingStore();
  
  const startMultipleLoading = (operation?: string) => setMultipleLoading(keys, true, operation);
  const stopMultipleLoading = () => setMultipleLoading(keys, false);
  
  return {
    isLoading: isAnyLoading(),
    startMultipleLoading,
    stopMultipleLoading,
  };
};

// Hook for global loading
export const useGlobalLoading = () => {
  const { globalLoading, setGlobalLoading } = useLoadingStore();
  
  return {
    globalLoading,
    setGlobalLoading,
  };
};

export default useLoadingStore; 